import { ArrowUpRight } from 'lucide-react';
import { type ComponentType, type CSSProperties, type ReactElement, useRef } from 'react';
import { Link } from 'react-router';

import { buttonVariants } from '@autop/ui/src/button';

import { poseToTransform, trackPose } from '@/shared/lib/sticky-scroll';
import { SCROLL_TRANSFORM_VAR, useStickyScroll } from '@/shared/lib/use-sticky-scroll';
import { cn } from '@/shared/lib/utils';
import { Highlight } from '@/shared/ui/highlight';

import { CARD_TRACKS, type CardSlot, heroLineTransform } from '../lib/hero-motion';
import { CommunityArt, InquiryArt, LibraryArt } from './hero-art';

/** SiteHeader 높이(h-16). 고정 프레임이 이 선 아래에 붙는다 */
const STICKY_TOP_PX = 64;

type CardTone = 'mint' | 'ink' | 'soft';

type HeroCard = {
  /** 넓은 화면에서 서는 자리 — 스크롤 궤적(CARD_TRACKS)을 이 이름으로 찾는다 */
  readonly slot: CardSlot;
  readonly eyebrow: string;
  readonly title: string;
  /** 보낼 경로. `null` 은 아직 열지 않은 갈래 — 링크 대신 「곧 오픈」 카드로 그린다 */
  readonly to: string | null;
  readonly tone: CardTone;
  readonly Art: ComponentType<{ className?: string }>;
  /** 좁은 화면에서 세로로 쌓을 때의 기울기 — 스크롤 연출 대신 정지된 부채꼴을 남긴다 */
  readonly stackedTilt: string;
};

/**
 * 카드 셋 = 오톱의 세 갈래. 열린 갈래의 경로는 전부 router.tsx 에 있다 — 죽은 링크를 넣지 않는다.
 * 커뮤니티는 다음 버전에서 열린다. 경로가 생기기 전까지 링크가 아니라 예고 카드다.
 * 문구는 기존 화면에 이미 쓰인 말에서 가져왔다 — 카피 확정 전까지 새로 짓지 않는다.
 */
const HERO_CARDS: readonly HeroCard[] = [
  {
    slot: 'left',
    eyebrow: '영상에서 소개한 그대로',
    title: '무료 자료실',
    to: '/library',
    tone: 'mint',
    Art: LibraryArt,
    stackedTilt: 'max-lg:-rotate-2',
  },
  {
    slot: 'center',
    eyebrow: '먼저 하고 있는 사람들과 같이',
    title: '커뮤니티',
    to: null,
    tone: 'ink',
    Art: CommunityArt,
    stackedTilt: 'max-lg:rotate-1',
  },
  {
    slot: 'right',
    eyebrow: '진단부터 실습, 정착까지',
    title: '기업 도입 문의',
    to: '/education/inquiry',
    tone: 'soft',
    Art: InquiryArt,
    stackedTilt: 'max-lg:-rotate-1',
  },
];

const TONE_CLASS: Record<CardTone, string> = {
  mint: 'bg-primary text-on-mint',
  ink: 'bg-ink-surface text-on-ink',
  soft: 'bg-mint-soft text-on-mint',
};

/** 화살표 칩은 카드 면과 반대 색으로 뒤집는다 — 같은 색이면 칩이 면에 묻힌다 */
const CHIP_CLASS: Record<CardTone, string> = {
  mint: 'bg-foreground text-background',
  ink: 'bg-primary text-on-mint',
  soft: 'bg-foreground text-background',
};

const GIANT_LINES = ['AUTOMATE', 'YOUR WORK'] as const;

const CARD_FACE_CLASS =
  'group relative flex aspect-[5/4] flex-col overflow-hidden rounded-[28px] p-7 lg:aspect-[10/11] lg:p-8 ' +
  'shadow-[0_30px_60px_-30px_rgb(6_17_11/0.45)] transition-shadow duration-300';

/** 카드 면의 내용 — 링크 카드와 예고 카드가 같이 쓴다 */
function HeroCardFace({ card }: { card: HeroCard }): ReactElement {
  const isOpen = card.to !== null;

  return (
    <>
      {/* 글자·칩은 일러스트보다 위 층에 둔다 — 좁은 카드에서 일러스트가 제목 밑까지 올라온다 */}
      <span className="relative z-10 text-[13.5px] font-medium tracking-[-0.02em] opacity-80">
        {card.eyebrow}
      </span>
      <span className="relative z-10 mt-1.5 text-[clamp(1.625rem,1.1rem+1.3vw,2.25rem)] font-bold leading-[1.15] tracking-[-0.05em]">
        {card.title}
      </span>

      <card.Art className="-right-[6%] -bottom-[4%] pointer-events-none absolute w-[60%] transition-transform duration-500 ease-out group-hover:-translate-y-2 group-hover:scale-[1.04] lg:w-[78%]" />

      {isOpen ? (
        <span
          aria-hidden="true"
          className={cn(
            'relative z-10 mt-auto grid size-11 place-items-center rounded-full transition-transform duration-300 group-hover:rotate-45',
            CHIP_CLASS[card.tone],
          )}
        >
          <ArrowUpRight className="size-5" strokeWidth={2} />
        </span>
      ) : (
        // 예고 카드는 화살표 대신 글자로 알린다 — 누를 곳이 없다는 것을 보조기기에도 전한다
        <span
          className={cn(
            'relative z-10 mt-auto self-start rounded-full px-4 py-2 text-[13px] font-semibold tracking-[-0.02em]',
            CHIP_CLASS[card.tone],
          )}
        >
          곧 오픈
        </span>
      )}
    </>
  );
}

function HeroCardItem({ card }: { card: HeroCard }): ReactElement {
  // 첫 페인트부터 모인 자세로 그린다 — 훅이 붙기 전 한 프레임 동안 카드가 가운데 겹쳐 튀지 않게
  const style = {
    [SCROLL_TRANSFORM_VAR]: poseToTransform(trackPose(CARD_TRACKS[card.slot], 0)),
  } as CSSProperties;

  return (
    <li
      data-scroll-card={card.slot}
      style={style}
      className={cn(
        'w-full max-w-md max-lg:mx-auto',
        'lg:absolute lg:top-1/2 lg:left-1/2 lg:w-[min(26vw,46svh,26rem)] lg:max-w-none',
        'lg:[transform:var(--scroll-transform)] lg:will-change-transform',
        // 모인 상태에서 가운데 카드가 맨 위에 온다 — 손에 쥔 카드 부채처럼
        card.slot === 'center' ? 'lg:z-20' : 'lg:z-10',
        card.stackedTilt,
      )}
    >
      {card.to !== null ? (
        <Link
          to={card.to}
          className={cn(
            CARD_FACE_CLASS,
            'hover:shadow-[0_40px_80px_-30px_rgb(6_17_11/0.55)]',
            'focus-visible:outline-[3px] focus-visible:outline-offset-4 focus-visible:outline-foreground',
            TONE_CLASS[card.tone],
          )}
        >
          <HeroCardFace card={card} />
        </Link>
      ) : (
        <div className={cn(CARD_FACE_CLASS, TONE_CLASS[card.tone])}>
          <HeroCardFace card={card} />
        </div>
      )}
    </li>
  );
}

/**
 * 홈 히어로 — 레퍼런스(대형 타이포 위에 기울어진 카드 3장)를 흰 바탕으로 옮겼다.
 *
 * 넓은 화면(lg+)에서는 섹션을 화면 높이보다 길게 두고 안쪽 프레임을 고정(sticky)시킨다.
 * 그 길이만큼 스크롤하는 동안 카드가 포개진 상태에서 펼쳐지고, 이어서 서로 다른 속도로 떠오른다
 * (lib/hero-motion). 좁은 화면에서는 고정 구간 없이 카드를 세로로 쌓는다 — 작은 화면에서
 * 스크롤을 붙잡아 두면 다음 내용으로 넘어가지 못하는 것처럼 느껴진다.
 */
export function HomeHero(): ReactElement {
  const sectionRef = useRef<HTMLElement>(null);
  useStickyScroll(sectionRef, {
    stickyTop: STICKY_TOP_PX,
    tracks: CARD_TRACKS,
    lineTransform: heroLineTransform,
  });

  return (
    <section ref={sectionRef} aria-labelledby="hero-heading" className="relative lg:h-[260svh]">
      <div className="flex flex-col lg:sticky lg:top-16 lg:h-[calc(100svh-4rem)] lg:overflow-hidden">
        <div className="flex justify-center pt-12 lg:pt-8">
          <span className="rounded-full border border-foreground/15 bg-background px-5 py-2 font-mono text-[12px] uppercase tracking-[0.2em]">
            Welcome to Autop
          </span>
        </div>

        {/* 제목 묶음은 DOM 에서 카드보다 앞에 둔다 — 스크린리더·키보드가 제목과 CTA 를 먼저 만난다.
            좁은 화면은 이 순서 그대로 보이고(카드 셋을 먼저 쌓으면 제목이 첫 화면 밖으로 밀린다),
            넓은 화면만 order-last 로 무대 아래에 내린다. 그 대가로 넓은 화면의 Tab 은
            아래 CTA → 가운데 카드 순서가 된다 — 제목이 카드보다 늦게 읽히는 것보다 낫다고 판단했다. */}
        <div className="u-container relative flex flex-col gap-7 pb-12 max-lg:order-2 lg:order-last lg:flex-row lg:z-30 lg:items-end lg:justify-between lg:pb-9">
          <div className="flex flex-col gap-3">
            <span className="text-[13px] font-medium text-muted-foreground">
              비전공자도 따라하면 완성됩니다
            </span>
            <h1
              id="hero-heading"
              className="text-[clamp(1.75rem,1.2rem+1.6vw,2.5rem)] font-bold leading-[1.2] tracking-[-0.045em]"
            >
              <Highlight>생산성</Highlight>을 높이는 가장 빠른 방법
            </h1>
            {/* 카피 확정 전 임시 문구 — 자료실·교육에 실제로 있는 것만 말한다 */}
            <p className="max-w-[34rem] text-[14.5px] leading-[1.75] text-muted-foreground">
              매주 반복되는 일을 자동화로 덜어냅니다. 템플릿을 받아 바로 쓰고, 막히면 영상을 보며
              따라 하면 됩니다.
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap gap-2">
            <Link to="/library" className={buttonVariants({ size: 'lg' })}>
              지금 시작하기
            </Link>
            <Link to="/education" className={buttonVariants({ size: 'lg', variant: 'secondary' })}>
              오톱 알아보기
            </Link>
          </div>
        </div>

        {/* 좁은 화면에서는 무대를 풀어(contents) 장식 타이포만 제목 위로 올린다.
            aria-hidden 이고 포커스를 받지 않아 읽기·Tab 순서에는 영향이 없다 */}
        <div className="relative max-lg:contents lg:flex-1">
          {/* 장식 타이포 — 읽히는 제목은 위 h1 이다 */}
          <p
            aria-hidden="true"
            className={cn(
              'pointer-events-none flex select-none flex-col items-center py-8 text-center max-lg:order-1 lg:absolute lg:inset-0 lg:justify-center lg:py-0',
              'whitespace-nowrap font-extralight leading-[0.84] tracking-[-0.06em]',
              'text-[17vw] lg:text-[min(14.5vw,24svh)]',
            )}
          >
            {GIANT_LINES.map((line) => (
              <span
                key={line}
                data-scroll-line=""
                className="block lg:[transform:var(--scroll-transform)] lg:will-change-transform"
              >
                {line}
              </span>
            ))}
          </p>

          <ul
            aria-label="바로가기"
            className="u-container flex flex-col gap-5 max-lg:order-3 max-lg:pb-16 lg:static lg:p-0"
          >
            {HERO_CARDS.map((card) => (
              <HeroCardItem key={card.slot} card={card} />
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
